<div
  #resourceWorkspace
  class="resource-workspace"
  [class.side-tray-layout]="!isFooterTrayLayout"
  [class.footer-tray-layout]="isFooterTrayLayout"
  [style.--resource-tray-size]="resourceTraySizePx + 'px'"
>
  <section class="resource-graph" aria-labelledby="resource-graph-title">
    <header class="resource-graph-heading">
      <h4 id="resource-graph-title">Resource Graph</h4>
      <button
        type="button"
        class="resource-graph-refresh"
        aria-label="Refresh Resource Graph"
        title="Refresh Resource Graph"
        [disabled]="refreshing"
        (click)="refreshRequested.emit()"
      >
        <span *ngIf="refreshing" class="resource-graph-refresh-spinner" aria-hidden="true"></span>
        <svg *ngIf="!refreshing" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v6h-6"></path></svg>
      </button>
    </header>
    <p class="graph-key">Each Root Component contributes an Ownership Tree. Dependency evidence stays in Resource details.</p>
    <p class="resource-graph-refresh-status" data-testid="resource-graph-refresh-status" role="status" *ngIf="refreshError">
      {{ refreshError }}
    </p>
    <div #treeTable class="tree-table" role="tree">
      <svg #connectorLayer class="tree-connector-layer" aria-hidden="true"></svg>
      <div class="tree-table-header" aria-hidden="true">
        <span></span>
        <span>Resource</span>
        <span>Kind</span>
        <span>Recorded state</span>
        <span>Availability</span>
      </div>
      <ng-template #graphRow let-row>
      <div
        class="graph-row"
        [class.selected]="selectedNode?.id === row.node.id"
        [class.construct-row]="row.node.kind === 'construct'"
        [class.tree-child]="row.depth > 0"
        [class.dependency-expanded]="row.dependencyExpanded"
        [attr.data-node-id]="row.node.id"
        data-testid="graph-node"
        role="treeitem"
      >
        <div class="inspection-trail-gutter">
          <ng-container *ngIf="row.node.kind === 'component' && trailStopsFor(row.node.component_id) as trailStops">
            <button
              *ngIf="trailStops.length === 1; else repeatedTrailStops"
              type="button"
              class="inspection-trail-badge"
              [class.origin]="trailStops[0].isOrigin"
              [class.current]="trailStops[0].isCurrent"
              [attr.aria-label]="'Return to dependency inspection stop ' + trailStops[0].index"
              (click)="inspectionTrailStopSelected.emit(trailStops[0].index)"
            ><span>{{ trailStops[0].index }}</span></button>
            <ng-template #repeatedTrailStops>
              <span class="inspection-trail-cluster">
                <button
                  type="button"
                  class="inspection-trail-badge repeated"
                  [class.origin]="hasOriginTrailStop(trailStops)"
                  [class.current]="hasCurrentTrailStop(trailStops)"
                  aria-label="Show dependency inspection stops"
                >…</button>
                <span class="inspection-trail-popover" role="dialog" aria-label="Dependency inspection stops">
                  <button
                    *ngFor="let trailStop of trailStops"
                    type="button"
                    class="inspection-trail-badge"
                    [class.origin]="trailStop.isOrigin"
                    [class.current]="trailStop.isCurrent"
                    (click)="inspectionTrailStopSelected.emit(trailStop.index)"
                  ><span>{{ trailStop.index }}</span></button>
                </span>
              </span>
            </ng-template>
          </ng-container>
        </div>
        <div class="tree-node">
          <span #treeControl class="tree-control" [style.margin-left.rem]="row.depth * 1.25">
            <button
              *ngIf="row.node.branch_capable"
              type="button"
              class="expand-button"
              [attr.aria-label]="(row.expanded ? 'Collapse ' : 'Expand ') + row.node.component_id"
              (click)="componentToggled.emit(row.node.component_id)"
            >
              <span *ngIf="!row.branchLoading" class="expand-glyph" aria-hidden="true">{{ row.expanded ? '−' : '+' }}</span>
              <span *ngIf="row.branchLoading" class="expand-spinner" aria-hidden="true"></span>
            </button>
            <span *ngIf="!row.node.branch_capable" class="leaf-marker" aria-label="Construct leaf">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3.5"></circle></svg>
            </span>
          </span>
          <button *ngIf="!row.detailPlaceholder" type="button" class="node-button" (click)="nodeSelected.emit(row.node)">
            <strong>{{ row.node.native_type || 'Type not recorded' }}</strong>
            <span class="node-name">{{ resourceId(row.node) }}</span>
            <span class="tree-warning" *ngIf="row.branchError" aria-label="Component retrieval failed">⚠ Failed</span>
          </button>
          <span *ngIf="row.detailPlaceholder" class="node-button">Gathering resource details…</span>
        </div>
        <span class="node-kind">{{ row.node.kind }}</span>
        <span class="recorded-state">{{ row.node.state || 'State not recorded' }}</span>
        <span class="graph-record-load-state">{{ row.recordLoadState }}</span>

      </div>
      </ng-template>

      <div class="graph-group-heading">Ownership Tree · Datastorio SaaS Tenant</div>
      <div class="ownership-tree-status-slot">
        <ng-content select="[ownership-tree-status]"></ng-content>
      </div>
      <ng-container *ngFor="let row of rows">
        <ng-container *ngIf="row.group === 'ownership'">
          <ng-container *ngTemplateOutlet="graphRow; context: { $implicit: row }"></ng-container>
        </ng-container>
      </ng-container>

      <div class="graph-group-heading">Ownership Trees · Detached Root Components</div>
      <div class="detached-components-status-slot">
        <ng-content select="[detached-components-status]"></ng-content>
      </div>
      <ng-container *ngFor="let row of rows">
        <ng-container *ngIf="row.group === 'detached'">
          <ng-container *ngTemplateOutlet="graphRow; context: { $implicit: row }"></ng-container>
        </ng-container>
      </ng-container>
    </div>
  </section>

  <div
    class="resource-tray-resizer"
    role="separator"
    tabindex="0"
    aria-label="Resize Resource Tray"
    [attr.aria-orientation]="isFooterTrayLayout ? 'horizontal' : 'vertical'"
    [attr.aria-valuemin]="minimumTraySizePx"
    [attr.aria-valuemax]="maximumTraySize(resourceWorkspace)"
    [attr.aria-valuenow]="resourceTraySizePx"
    (pointerdown)="startTrayResize($event, resourceWorkspace)"
    (keydown)="resizeTrayWithKeyboard($event, resourceWorkspace)"
  ></div>

  <div class="resource-details-tray">
    <ng-content select="[resource-tray]"></ng-content>
  </div>
</div>
